import { useEffect, useState } from 'react';
import api from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { crc, fechaHora, METODOS_PAGO } from '../utils/format.js';
import { tieneModulo } from '../config/modulos.js';
import { PageHeader, Modal, Loading, EmptyState } from '../components/UI.jsx';
import { Icon } from '../components/Icons.jsx';

// Pantalla de cierres de caja: abrir turno, ver el acumulado en vivo y
// cerrar contando el efectivo (esperado vs contado). El backend calcula
// los totales; aca solo se muestran y, para la vista previa del cierre,
// se calcula la diferencia con lo que el usuario va tecleando.
//
// Contrato real (GET /cajas/actual): la fila de "cajas" (monto_inicial,
// notas_apertura, abierta_at, usuario_id...) mas un objeto "acumulado"
// con { total_ventas, ventas_efectivo, ventas_tarjeta, ventas_sinpe,
// ventas_transferencia, esperado_efectivo } calculado en vivo. El
// historico (GET /cajas) trae { items, pagina, limite, total }.

// De metodo de pago (efectivo/tarjeta/...) al nombre de columna que usa el backend.
const CAMPO_ACUMULADO = {
  efectivo: 'ventas_efectivo',
  tarjeta: 'ventas_tarjeta',
  sinpe: 'ventas_sinpe',
  transferencia: 'ventas_transferencia',
};

export default function Caja() {
  const toast = useToast();

  // undefined = cargando, null = sin caja abierta (o error), objeto = caja abierta
  const [actual, setActual] = useState(undefined);
  const [errorActual, setErrorActual] = useState('');

  // undefined = cargando, null = error, array = historico
  const [historial, setHistorial] = useState(undefined);
  const [errorHistorial, setErrorHistorial] = useState('');
  const [desdeH, setDesdeH] = useState('');
  const [hastaH, setHastaH] = useState('');

  const [modalAbrir, setModalAbrir] = useState(false);
  const [formAbrir, setFormAbrir] = useState({ monto_inicial: '', notas: '' });
  const [guardandoAbrir, setGuardandoAbrir] = useState(false);

  const [modalCerrar, setModalCerrar] = useState(false);
  const [montoContado, setMontoContado] = useState('');
  const [notasCierre, setNotasCierre] = useState('');
  const [guardandoCierre, setGuardandoCierre] = useState(false);

  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const cargarActual = () => {
    setErrorActual('');
    api.get('/cajas/actual')
      .then((r) => setActual(r.data.data))
      .catch((err) => {
        setActual(null);
        setErrorActual(err.response?.data?.message || 'No se pudo cargar el estado de la caja');
      });
  };

  const cargarHistorial = () => {
    setErrorHistorial('');
    const params = {};
    if (desdeH) params.desde = desdeH;
    if (hastaH) params.hasta = hastaH;
    api.get('/cajas', { params })
      .then((r) => setHistorial(r.data.data?.items || []))
      .catch((err) => {
        setHistorial(null);
        setErrorHistorial(err.response?.data?.message || 'No se pudo cargar el historico de cierres');
      });
  };

  useEffect(cargarActual, []);
  useEffect(cargarHistorial, [desdeH, hastaH]);

  const abrirModalAbrir = () => {
    setFormAbrir({ monto_inicial: '', notas: '' });
    setModalAbrir(true);
  };

  const confirmarAbrir = async () => {
    if (formAbrir.monto_inicial === '' || Number(formAbrir.monto_inicial) < 0) {
      toast.error('Ingresa el monto inicial');
      return;
    }
    setGuardandoAbrir(true);
    try {
      await api.post('/cajas/abrir', {
        monto_inicial: Number(formAbrir.monto_inicial),
        notas: formAbrir.notas || '',
      });
      toast.success('Caja abierta');
      setModalAbrir(false);
      cargarActual();
      cargarHistorial();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo abrir la caja');
    } finally {
      setGuardandoAbrir(false);
    }
  };

  const abrirModalCerrar = () => {
    setMontoContado('');
    setNotasCierre('');
    setModalCerrar(true);
  };

  const acumulado = actual?.acumulado || {};
  const esperado = Number(acumulado.esperado_efectivo || 0);
  const diferencia = montoContado !== '' && !Number.isNaN(Number(montoContado))
    ? Number(montoContado) - esperado
    : null;

  const confirmarCerrar = async () => {
    if (montoContado === '' || Number(montoContado) < 0) {
      toast.error('Ingresa el efectivo contado');
      return;
    }
    setGuardandoCierre(true);
    try {
      await api.post('/cajas/cerrar', {
        monto_contado: Number(montoContado),
        notas: notasCierre || '',
      });
      toast.success('Caja cerrada');
      setModalCerrar(false);
      cargarActual();
      cargarHistorial();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo cerrar la caja');
    } finally {
      setGuardandoCierre(false);
    }
  };

  const verDetalle = async (id) => {
    setCargandoDetalle(true);
    setDetalle({ id });
    try {
      const { data } = await api.get(`/cajas/${id}`);
      setDetalle(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo cargar el cierre');
      setDetalle(null);
    } finally {
      setCargandoDetalle(false);
    }
  };

  return (
    <div>
      <PageHeader title="Cierres de caja" subtitle="Apertura, turno en vivo y arqueo de cierre" />

      {errorActual && (
        <div
          className="card"
          style={{ padding: 14, marginBottom: 16, borderColor: 'var(--danger)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Icon.alert width={18} height={18} style={{ color: 'var(--danger)', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 14 }}>{errorActual}</span>
          <button className="btn btn-outline btn-sm" onClick={cargarActual}>Reintentar</button>
        </div>
      )}

      {actual === undefined && !errorActual ? (
        <Loading texto="Cargando estado de caja..." />
      ) : actual == null ? (
        !errorActual && (
          <div className="card" style={{ padding: '50px 20px', textAlign: 'center', marginBottom: 24 }}>
            <div style={{ color: 'var(--text-muted)', display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
              <Icon.caja width={42} height={42} />
            </div>
            <h3 style={{ marginBottom: 6 }}>No hay caja abierta</h3>
            <p className="muted" style={{ marginBottom: 20 }}>Abre la caja con el monto inicial para arrancar el turno.</p>
            <button className="btn btn-primary" onClick={abrirModalAbrir} style={{ margin: '0 auto' }}>
              <Icon.plus /> Abrir caja
            </button>
          </div>
        )
      ) : (
        <div className="card" style={{ padding: 22, marginBottom: 24 }}>
          <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <div>
              <span className="badge badge-success">
                <Icon.check width={13} height={13} /> Caja abierta
              </span>
              <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                Desde {fechaHora(actual.abierta_at)} · Abrio {actual.usuario_nombre || (actual.usuario_id ? `Usuario #${actual.usuario_id}` : '—')}
              </p>
            </div>
            <button className="btn btn-primary" onClick={abrirModalCerrar}>
              <Icon.caja /> Cerrar caja
            </button>
          </div>

          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 20 }}>
            <div>
              <span className="muted" style={{ fontSize: 13 }}>Monto inicial</span>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)' }}>{crc(actual.monto_inicial)}</div>
            </div>
            {/* De donde vino la plata del turno. Antes era un solo numero y los
                abonos de reparacion, aunque se sumaban, no se veian por ningun lado. */}
            <div>
              <span className="muted" style={{ fontSize: 13 }}>Ventas de contado</span>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--accent-texto)' }}>{crc(acumulado.ventas_contado || 0)}</div>
            </div>
            <div>
              <span className="muted" style={{ fontSize: 13 }}>Cobros de credito</span>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--accent-texto)' }}>{crc(acumulado.cobros_credito || 0)}</div>
            </div>
            {tieneModulo('reparaciones') && (
              <div>
                <span className="muted" style={{ fontSize: 13 }}>Abonos de reparaciones</span>
                <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--accent-texto)' }}>{crc(acumulado.abonos_reparacion || 0)}</div>
              </div>
            )}
            <div>
              <span className="muted" style={{ fontSize: 13 }}>Gastos en efectivo</span>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--danger)' }}>{crc(acumulado.gastos_efectivo || 0)}</div>
            </div>
          </div>

          <span className="muted" style={{ fontSize: 13 }}>Desglose por metodo de pago</span>
          <div className="grid-2" style={{ gap: 10, marginTop: 8 }}>
            {METODOS_PAGO.map((m) => (
              <div key={m.value} className="card" style={{ padding: 12, background: 'var(--surface-2)' }}>
                <div className="muted" style={{ fontSize: 12 }}>{m.label}</div>
                <div style={{ fontWeight: 700 }}>{crc(acumulado[CAMPO_ACUMULADO[m.value]] || 0)}</div>
              </div>
            ))}
          </div>

          {actual.notas_apertura && (
            <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>Notas: {actual.notas_apertura}</p>
          )}
        </div>
      )}

      <div className="flex items-center justify-between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <h3 style={{ margin: 0 }}>Historial de cierres</h3>
        <div className="flex items-center gap-sm" style={{ flexWrap: 'wrap' }}>
          <input className="input" type="date" value={desdeH} aria-label="Cierres desde"
            style={{ width: 'auto' }} onChange={(e) => setDesdeH(e.target.value)} />
          <span className="muted" style={{ fontSize: 13 }}>a</span>
          <input className="input" type="date" value={hastaH} aria-label="Cierres hasta"
            style={{ width: 'auto' }} onChange={(e) => setHastaH(e.target.value)} />
          {(desdeH || hastaH) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setDesdeH(''); setHastaH(''); }}>
              Quitar fechas
            </button>
          )}
        </div>
      </div>
      {errorHistorial ? (
        <div className="card" style={{ padding: 14, borderColor: 'var(--danger)' }}>
          <span style={{ fontSize: 14 }}>{errorHistorial}</span>
        </div>
      ) : historial === undefined ? (
        <Loading texto="Cargando historico..." />
      ) : historial && historial.length === 0 ? (
        <EmptyState icon="caja" titulo="Sin cierres todavia" texto="Cuando cierres tu primer turno, va a aparecer aqui." />
      ) : historial ? (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Apertura</th>
                <th>Cierre</th>
                <th>Cajero</th>
                <th className="text-right">Inicial</th>
                <th className="text-right">Esperado</th>
                <th className="text-right">Contado</th>
                <th className="text-right">Diferencia</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {historial.map((c) => {
                const esp = Number(c.esperado_efectivo || 0);
                const dif = Number(c.diferencia ?? (Number(c.monto_contado || 0) - esp));
                const color = dif > 0 ? 'var(--success)' : dif < 0 ? 'var(--danger)' : 'var(--text-soft)';
                return (
                  <tr key={c.id}>
                    <td className="muted">{fechaHora(c.abierta_at)}</td>
                    <td className="muted">{c.cerrada_at ? fechaHora(c.cerrada_at) : '—'}</td>
                    <td className="muted">{c.usuario_nombre || '—'}</td>
                    <td className="text-right mono">{crc(c.monto_inicial)}</td>
                    <td className="text-right mono">
                      {crc(esp)}
                      {c.en_curso && (
                        <span className="badge" style={{ marginLeft: 6, background: 'var(--info-bg)', color: 'var(--info)' }}>
                          En curso
                        </span>
                      )}
                    </td>
                    <td className="text-right mono">{c.monto_contado != null ? crc(c.monto_contado) : '—'}</td>
                    <td className="text-right mono" style={{ fontWeight: 700, color }}>
                      {c.monto_contado != null ? `${dif > 0 ? '+' : ''}${crc(dif)}` : '—'}
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => verDetalle(c.id)}>Ver</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* ---- Abrir caja ---- */}
      <Modal open={modalAbrir} onClose={() => setModalAbrir(false)} title="Abrir caja" width={420}>
        <div className="field">
          <label>Monto inicial (₡) *</label>
          <input
            className="input"
            type="number"
            value={formAbrir.monto_inicial}
            onChange={(e) => setFormAbrir({ ...formAbrir, monto_inicial: e.target.value })}
            autoFocus
          />
        </div>
        <div className="field">
          <label>Notas</label>
          <textarea
            className="input"
            rows={3}
            value={formAbrir.notas}
            onChange={(e) => setFormAbrir({ ...formAbrir, notas: e.target.value })}
          />
        </div>
        <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={() => setModalAbrir(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={confirmarAbrir} disabled={guardandoAbrir}>
            {guardandoAbrir ? <span className="spinner" /> : 'Abrir caja'}
          </button>
        </div>
      </Modal>

      {/* ---- Cerrar caja ---- */}
      <Modal open={modalCerrar} onClose={() => setModalCerrar(false)} title="Cerrar caja" width={460}>
        <div className="field">
          <label>Efectivo contado (₡) *</label>
          <input
            className="input"
            type="number"
            value={montoContado}
            onChange={(e) => setMontoContado(e.target.value)}
            autoFocus
          />
        </div>

        {/* De donde sale lo que deberia haber en el cajon, linea a linea:
            un numero de plata que no se puede explicar no sirve para cuadrar. */}
        <div className="card" style={{ padding: 16, marginBottom: 12, background: 'var(--surface-2)', fontSize: 14 }}>
          {[
            ['Monto inicial (con que se abrio)', actual?.monto_inicial, ''],
            ['Ventas de contado en efectivo', acumulado.efectivo_ventas, '+'],
            ['Cobros de credito en efectivo', acumulado.efectivo_cobros, '+'],
            ...(tieneModulo('reparaciones') ? [['Abonos de reparaciones en efectivo', acumulado.efectivo_abonos, '+']] : []),
            ['Gastos pagados en efectivo', acumulado.gastos_efectivo, '−'],
          ].map(([etiqueta, valor, signo]) => (
            <div key={etiqueta} className="flex justify-between" style={{ marginBottom: 6 }}>
              <span className="muted">{signo && <span className="mono" style={{ display: 'inline-block', width: 14 }}>{signo}</span>}{etiqueta}</span>
              <span className="mono">{crc(Number(valor || 0))}</span>
            </div>
          ))}
          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '10px 0' }} />
          <div className="flex justify-between" style={{ fontWeight: 700 }}>
            <span>Efectivo que deberia haber</span>
            <span className="mono">{crc(esperado)}</span>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Tarjeta, SINPE y transferencia no se cuentan: esa plata no entra al cajon.
          </p>
        </div>

        <div className="card" style={{ padding: 16, marginBottom: 14, background: 'var(--surface-2)' }}>
          <div className="flex justify-between" style={{ marginBottom: 8 }}>
            <span className="muted">Deberia haber</span>
            <span className="mono" style={{ fontWeight: 600 }}>{crc(esperado)}</span>
          </div>
          <div className="flex justify-between">
            <span className="muted">Contado</span>
            <span className="mono" style={{ fontWeight: 600 }}>{montoContado !== '' ? crc(montoContado) : '—'}</span>
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '12px 0' }} />
          <div className="flex justify-between items-center">
            <span style={{ fontWeight: 700 }}>
              {diferencia === null ? 'Diferencia' : diferencia > 0 ? 'Sobrante' : diferencia < 0 ? 'Faltante' : 'Cuadrado'}
            </span>
            <span
              style={{
                fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-display)',
                color: diferencia === null || diferencia === 0
                  ? 'var(--text-soft)'
                  : diferencia > 0 ? 'var(--success)' : 'var(--danger)',
              }}
            >
              {diferencia === null ? '—' : `${diferencia > 0 ? '+' : ''}${crc(diferencia)}`}
            </span>
          </div>
        </div>

        <div className="field">
          <label>Notas</label>
          <textarea className="input" rows={2} value={notasCierre} onChange={(e) => setNotasCierre(e.target.value)} />
        </div>

        <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={() => setModalCerrar(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={confirmarCerrar} disabled={guardandoCierre}>
            {guardandoCierre ? <span className="spinner" /> : 'Confirmar cierre'}
          </button>
        </div>
      </Modal>

      {/* ---- Detalle de un cierre historico ---- */}
      <Modal open={!!detalle} onClose={() => setDetalle(null)} title={`Cierre #${detalle?.id ?? ''}`} width={520}>
        {cargandoDetalle ? (
          <Loading />
        ) : detalle ? (
          <div>
            <div className="grid-2" style={{ gap: 12, marginBottom: 16 }}>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>Apertura</span>
                <div>{fechaHora(detalle.abierta_at)}</div>
              </div>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>Cierre</span>
                <div>{detalle.cerrada_at ? fechaHora(detalle.cerrada_at) : '—'}</div>
              </div>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>Inicial</span>
                <div className="mono">{crc(detalle.monto_inicial)}</div>
              </div>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>Contado</span>
                <div className="mono">{detalle.monto_contado != null ? crc(detalle.monto_contado) : '—'}</div>
              </div>
            </div>
            {detalle.diferencia != null && (
              <div
                className="flex justify-between items-center"
                style={{
                  padding: '10px 14px', borderRadius: 10, marginBottom: 16, background: 'var(--surface-2)',
                }}
              >
                <span style={{ fontWeight: 600 }}>Diferencia</span>
                <span
                  className="mono"
                  style={{
                    fontWeight: 700,
                    color: detalle.diferencia > 0 ? 'var(--success)' : detalle.diferencia < 0 ? 'var(--danger)' : 'var(--text-soft)',
                  }}
                >
                  {detalle.diferencia > 0 ? '+' : ''}{crc(detalle.diferencia)}
                </span>
              </div>
            )}
            {Array.isArray(detalle.ventas) && detalle.ventas.length > 0 ? (
              <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table className="data">
                  <thead>
                    <tr><th>Venta</th><th>Metodo</th><th className="text-right">Total</th></tr>
                  </thead>
                  <tbody>
                    {detalle.ventas.map((v) => (
                      <tr key={`v-${v.id}`}>
                        <td>{v.numero}</td>
                        <td className="muted" style={{ textTransform: 'capitalize' }}>{v.metodo_pago}</td>
                        <td className="text-right mono">{crc(v.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>Sin ventas de contado en este turno.</p>
            )}
            {Array.isArray(detalle.cobros) && detalle.cobros.length > 0 ? (
              <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table className="data">
                  <thead>
                    <tr><th>Cobro credito</th><th>Metodo</th><th className="text-right">Abono</th></tr>
                  </thead>
                  <tbody>
                    {detalle.cobros.map((c) => (
                      <tr key={`c-${c.id}`}>
                        <td>{c.venta_numero}</td>
                        <td className="muted" style={{ textTransform: 'capitalize' }}>{c.metodo}</td>
                        <td className="text-right mono">{crc(c.monto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {Array.isArray(detalle.reparaciones) && detalle.reparaciones.length > 0 ? (
              <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table className="data">
                  <thead>
                    <tr><th>Reparacion</th><th>Metodo</th><th className="text-right">Abono</th></tr>
                  </thead>
                  <tbody>
                    {detalle.reparaciones.map((a) => (
                      <tr key={`r-${a.id}`}>
                        <td>{a.reparacion_numero}</td>
                        <td className="muted" style={{ textTransform: 'capitalize' }}>{a.metodo}</td>
                        <td className="text-right mono">{crc(a.monto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {Array.isArray(detalle.gastos) && detalle.gastos.length > 0 ? (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr><th>Gasto</th><th>Metodo</th><th className="text-right">Monto</th></tr>
                  </thead>
                  <tbody>
                    {detalle.gastos.map((g) => (
                      <tr key={`g-${g.id}`}>
                        <td>{g.descripcion}</td>
                        <td className="muted" style={{ textTransform: 'capitalize' }}>{g.metodo_pago}</td>
                        <td className="text-right mono">{crc(g.monto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
